import React from 'react';
import type { AskCitation } from '../../lib/assistantApi';
import styles from './CitationCards.module.css';

interface CitationCardsProps {
  citations: AskCitation[];
  hrefFor: (slug: string, anchor?: string) => string;
  onNavigate: (slug: string, anchor?: string) => void;
}

/** Plain clicks stay in the modal's flow; modified clicks open a new tab as usual. */
export function isNewTabClick(event: React.MouseEvent): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1;
}

export const CitationCards: React.FC<CitationCardsProps> = ({ citations, hrefFor, onNavigate }) => {
  if (citations.length === 0) return null;

  const pages: Array<{ slug: string; title: string; items: AskCitation[] }> = [];
  for (const citation of citations) {
    const page = pages.find((entry) => entry.slug === citation.slug);
    if (page) page.items.push(citation);
    else pages.push({ slug: citation.slug, title: citation.title, items: [citation] });
  }

  const linkProps = (citation: AskCitation) => ({
    href: hrefFor(citation.slug, citation.anchor || undefined),
    onClick: (event: React.MouseEvent) => {
      if (isNewTabClick(event)) return;
      event.preventDefault();
      onNavigate(citation.slug, citation.anchor || undefined);
    },
  });

  return (
    <div className={styles.row}>
      <span className={styles.label}>Sources</span>
      {pages.map((page) => (
        <span key={page.slug} className={styles.pill}>
          {page.items.map((citation) => {
            const heading =
              citation.heading && citation.heading !== citation.title ? citation.heading : null;
            const label = heading ? `${citation.title} — ${heading}` : citation.title;
            return (
              <a
                key={`${citation.id}-${citation.chunk_id || citation.slug}`}
                {...linkProps(citation)}
                title={label}
                aria-label={`Source ${citation.id}: ${label}`}
                className={styles.marker}
              >
                {citation.id}
              </a>
            );
          })}
          <a {...linkProps(page.items[0])} title={page.title} className={styles.title}>
            {page.title}
          </a>
        </span>
      ))}
    </div>
  );
};

export default CitationCards;
